import styled from "styled-components";
import moment from "moment-timezone";
import { useState } from "react";
import { RAImage, RATabs, RATypography } from "@/components/customs";
import { TailNumber } from "@/components/molecules";
import { Row, Col, Modal, Divider, Grid } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import { SvgEdit } from "@/assets/icons";
import { JET_ONE_SUITES_OPTIONS, JET_ONE_SUITES_STATUS, NOTIFICATION_TYPE } from "@/config/constants";
import { ModalCancelJ1SBooking } from "../ModalCancelJ1SBooking";
import { cancelBooking } from "@/stores/apis/apiJetOneSuites";
import { toast } from "@/modules/toast";
import { ImgEmptyBox } from "@/assets/images";
import { useSelector } from "react-redux";
import { selectAuthState, selectFormatDateTimeFull } from "@/stores/slices/authSlice";
import { CardFlightInfo } from "./CardFlightInfo";
const { useBreakpoint } = Grid;

export function ModalBookingDetails({
	visible = false,
	onClose,
	booking = {},
	onPressEditBooking,
	onCancelBookingSuccess,
}) {
	const formatDTFull = useSelector(selectFormatDateTimeFull);
	const screens = useBreakpoint();
	const { user } = useSelector(selectAuthState);
	const { type, aircraft = {} } = booking;
	const [showConfirmCancel, setShowConfirmCancel] = useState({
		visible: false,
		isBoth: false,
		departure: null,
		arrival: null,
	});
	const [currentTab, setCurrentTab] = useState("");

	const onCancelBooking = async (type, bookingId) => {
		let isBoth = false;
		if (!type || type === JET_ONE_SUITES_OPTIONS.BOTH) {
			isBoth = true;
		}
		const payload = {
			is_both: isBoth,
		};
		const rs = await cancelBooking(bookingId, payload);
		if (rs && rs.success) {
			onCancelBookingSuccess?.(rs?.data ?? booking);
			toast({
				type: NOTIFICATION_TYPE.SUCCESS,
				description: "Booking Has Been Cancelled Successfully.",
			});
		} else {
			const messageError = rs.errors?.message || "Booking Cancellation Failed.";
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: messageError,
			});
		}

		setShowConfirmCancel({
			visible: false,
			isBoth: false,
			departure: null,
			arrival: null,
		});
	};

	const checkAllowEdit = () => {
		const departure = booking?.depart_datetime ? booking : booking?.related_request;
		const arrival = booking?.return_datetime ? booking : booking?.related_request;

		if (user?.level !== 3) {
			return false;
		}

		let allow = true;
		if (
			(type === JET_ONE_SUITES_OPTIONS.DEPARTURE &&
				(!departure?.status ||
					departure?.status === JET_ONE_SUITES_STATUS.CANCELLED ||
					departure?.status === JET_ONE_SUITES_STATUS.REJECTED)) ||
			(type === JET_ONE_SUITES_OPTIONS.RETURN &&
				(!arrival?.status ||
					arrival?.status === JET_ONE_SUITES_STATUS.CANCELLED ||
					arrival?.status === JET_ONE_SUITES_STATUS.REJECTED)) ||
			(type === JET_ONE_SUITES_OPTIONS.BOTH &&
				(!departure?.status ||
					departure?.status === JET_ONE_SUITES_STATUS.CANCELLED ||
					departure?.status === JET_ONE_SUITES_STATUS.REJECTED) &&
				(!arrival?.status ||
					arrival?.status === JET_ONE_SUITES_STATUS.CANCELLED ||
					arrival?.status === JET_ONE_SUITES_STATUS.REJECTED))
		) {
			allow = false;
		}

		return allow;
	};

	const onShowConfirmCancel = () => {
		const departure = booking?.depart_datetime ? booking : booking?.related_request;
		const arrival = booking?.return_datetime ? booking : booking?.related_request;
		let isBoth = false;
		if (
			type === JET_ONE_SUITES_OPTIONS.BOTH &&
			departure?.status !== JET_ONE_SUITES_STATUS.CANCELLED &&
			departure?.status !== JET_ONE_SUITES_STATUS.REJECTED &&
			arrival?.status !== JET_ONE_SUITES_STATUS.CANCELLED &&
			arrival?.status !== JET_ONE_SUITES_STATUS.REJECTED
		) {
			isBoth = true;
		}
		setShowConfirmCancel({ visible: true, isBoth, departure, arrival });
	};

	const _renderFlightDeleted = () => {
		return (
			<WrapperEmpty>
				<RAImage url={ImgEmptyBox} width="150px" height="104px" />
				<RATypography variant="sub-heading4" color="#222222" top="16px">
					Oops! This leg was deleted.
				</RATypography>
			</WrapperEmpty>
		);
	};

	const _renderFlightDetailsByType = () => {
		if (type === JET_ONE_SUITES_OPTIONS.DEPARTURE) {
			const flight = booking.depart_datetime ? booking : booking?.related_request;
			return (
				<>
					<Divider />
					<CardFlightInfo
						flightTime={flight?.depart_datetime}
						flight={flight}
						flightType="DEPARTURE"
						onShowConfirmCancel={() => onShowConfirmCancel(flight?._id)}
					/>
				</>
			);
		} else if (type === JET_ONE_SUITES_OPTIONS.RETURN) {
			const flight = booking.return_datetime ? booking : booking?.related_request;
			return (
				<>
					<Divider />
					<CardFlightInfo
						flightTime={flight?.return_datetime}
						flight={flight}
						flightType="ARRIVAL"
						onShowConfirmCancel={() => onShowConfirmCancel(flight?._id)}
					/>
				</>
			);
		} else if (type === JET_ONE_SUITES_OPTIONS.BOTH) {
			const departure = booking.depart_datetime ? booking : booking?.related_request;
			const arrival = booking.return_datetime ? booking : booking?.related_request;
			const tabRenderer = [
				{
					key: "DEPARTURE",
					label: "Departure",
					render: !departure ? (
						_renderFlightDeleted()
					) : (
						<CardFlightInfo
							flightTime={departure?.depart_datetime}
							flight={departure}
							flightType="DEPARTURE"
							onShowConfirmCancel={() => onShowConfirmCancel(departure?._id)}
						/>
					),
				},
				{
					key: "ARRIVAL",
					label: "Arrival",
					render: (
						<CardFlightInfo
							flightTime={departure?.return_datetime}
							flight={arrival}
							flightType="ARRIVAL"
							onShowConfirmCancel={() => onShowConfirmCancel(arrival?._id)}
						/>
					),
				},
			];
			return (
				<div style={{ marginTop: "8px" }}>
					<RATabs tabRenderer={tabRenderer} currentTab={currentTab} onChange={(tab) => setCurrentTab(tab)} />
				</div>
			);
		}
	};

	const _renderFlightInfoByType = () => {
		const { name = "", tail_number = "", images = [] } = aircraft;
		return (
			<WrapperFlightInfo>
				<Row gutter={20} align="middle">
					<Col span={8}>
						<RAImage
							width="100%"
							height="88px"
							url={images && images[0] ? images[0].url : "/images/example/aircraft_1.png"}
							borderRadius="8px"
						/>
					</Col>
					<Col span={16}>
						<Row justify="space-between" align="top" style={{ flexWrap: "nowrap" }}>
							<Col>
								<RATypography variant={screens.xs ? "sub-heading3" : "sub-heading2"} maxLines={2}>
									{name}
								</RATypography>
							</Col>
							<Col>
								{checkAllowEdit() && (
									<WrapperButton
										data-testid="BookingDetailsModal_editBtn"
										onClick={() => onPressEditBooking?.(booking)}
									>
										<SvgEdit />
									</WrapperButton>
								)}
							</Col>
						</Row>

						<Row justify="space-between" align="middle">
							<Col>
								<Row>
									<TailNumber tail={tail_number} />
								</Row>
							</Col>
							<Col>
								<Row justify="end">
									<RATypography variant="body4" color="#595959">
										Created at
									</RATypography>
								</Row>
								<Row justify="end">
									<RATypography variant="sub-heading5">
										{moment(booking?.created_at).format(formatDTFull)}
									</RATypography>
								</Row>
							</Col>
						</Row>
					</Col>
				</Row>
				{_renderFlightDetailsByType()}
			</WrapperFlightInfo>
		);
	};

	return (
		<StyledModal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => onClose(false)}
			footer={null}
			wrapClassName="auth-wrapper"
			width="auto"
			style={{ padding: 0, margin: "20px 0px" }}
			maskClosable={false}
		>
			<ContainerBookingDetails data-testid="BookingDetailsModal_content">
				<Row>
					<Col span={24}>
						<WrapperContent>
							<RATypography variant="h3">Booking Details</RATypography>
						</WrapperContent>
					</Col>
				</Row>
				<Divider />
				{_renderFlightInfoByType()}
			</ContainerBookingDetails>

			{/* Confirm Modal */}
			<ModalCancelJ1SBooking
				visible={showConfirmCancel.visible}
				isBoth={showConfirmCancel.isBoth}
				departureId={showConfirmCancel.departure?._id}
				arrivalId={showConfirmCancel.arrival?._id}
				onClose={() =>
					setShowConfirmCancel({
						visible: false,
						isBoth: false,
						departure: null,
						arrival: null,
					})
				}
				onConfirm={onCancelBooking}
				title="Confirm Cancellation"
				note="Are you sure you want to cancel this booking?"
			/>
		</StyledModal>
	);
}

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background-color: #ffffff;
		border-radius: 24px;
		box-shadow: none;
	}
	.ant-modal-close {
		width: 28px;
		height: 28px;
		top: 22px;
		right: 22px;
		@media (max-width: 576px) {
			top: 22px;
			right: 4px;
		}
	}
	.ant-modal-body {
		overflow-y: auto;
		overflow-x: hidden;
		max-height: 80vh;
	}
`;

const ContainerBookingDetails = styled.div`
	width: 50vw;
	max-width: 720px;
	min-width: 343px;
	padding: clamp(24px, 8vw, 60px) clamp(16px, 4vw, 60px);
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const WrapperFlightInfo = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 20px;
`;
const WrapperButton = styled.div`
	border-radius: 4px;
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
`;
const WrapperEmpty = styled.div`
	padding: 40px 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;
